import { HttpStatusCode } from "axios";
import { TFunction } from "i18next";

import {
  ApiException,
  IReportCriteriaFilters,
  IReportCriteriaFiltersSchemaValues,
  ReportsApi,
  TimeZoneEnum,
  trimSeconds,
} from "@ryde/shared";

import {
  DashboardChartType,
  IDashboardCard,
  IDashboardStatData,
} from "../dynamic-dashboard.models";
import { EMPTY_CONTENT } from "./dashboard-card-data.constants";
import {
  ICardDataFetchState,
  TCalendarDates,
} from "./dashboard-card-data.models";
import {
  buildCriteria,
  buildUserFilters,
  mergeCardFilters,
  reduceCardData,
} from "./dashboard-card-data.utils";

interface IFetchCardDataParams {
  card: IDashboardCard;
  calendarDates: TCalendarDates;
  appliedFilters: IReportCriteriaFiltersSchemaValues;
  t: TFunction;
  timezone: TimeZoneEnum;
  abort: AbortController;
}

/**
 * Runs a custom card's report fetch through the reports engine — which enforces
 * report/field/row-level permissions server-side — and reduces the result into
 * the chart state. Kept out of the hook so the effect stays a thin orchestrator
 * (start → setState, error → mapFetchError).
 *
 * One request per card: a Stat card used to fetch the previous period too, for a
 * % comparison the card cannot render. The shared subheader is built around a
 * named card's `dashboard.<CARD>.SUBTITLE` and "vs last <weekday>" copy, neither
 * of which fits a report metric compared over the previous PERIOD — so the number
 * was computed, carried, and thrown away, at the cost of a second report query.
 */
export const fetchDashboardCardData = async ({
  card,
  calendarDates,
  appliedFilters,
  t,
  timezone,
  abort,
}: IFetchCardDataParams): Promise<ICardDataFetchState> => {
  const isStat = card.chartType === DashboardChartType.Stat;
  const userFilters = buildUserFilters(
    mergeCardFilters({ appliedFilters, cardFilters: card.cardFilters }),
  );
  const currentFilters: IReportCriteriaFilters = { ...userFilters };
  if (calendarDates) {
    currentFilters.dates = {
      value: [
        trimSeconds(calendarDates.date1),
        trimSeconds(calendarDates.date2),
      ],
    };
  }

  const current = await ReportsApi.fetchReportData(
    buildCriteria(card, currentFilters),
    abort,
  );

  if (!current?.success || !current.data) {
    return {
      content: EMPTY_CONTENT,
      status: { loading: false, isEmpty: false, error: true },
    };
  }

  const reduced = reduceCardData(card, current.data, t, timezone);

  let stat: IDashboardStatData | undefined;
  if (isStat && reduced.statValue !== undefined) {
    stat = { value: reduced.statValue, unit: reduced.statUnit };
  }

  return {
    content: reduced.content,
    multiSeries: reduced.multiSeries,
    stat,
    valueFormatter: reduced.valueFormatter,
    status: {
      loading: false,
      isEmpty: reduced.isEmpty,
      metricDenied: reduced.metricDenied,
    },
  };
};

/** Map a fetch rejection to the card's error / forbidden (403) state. */
export const mapFetchError = (err: unknown): ICardDataFetchState => {
  const forbidden =
    (err as ApiException)?.statusCode === HttpStatusCode.Forbidden;
  return {
    content: EMPTY_CONTENT,
    status: { loading: false, isEmpty: false, forbidden, error: !forbidden },
  };
};
